import { PALETTE } from '@overlayqa/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Alert02Icon, Clock01Icon, LockedIcon, ArrowRight01Icon, Cancel01Icon } from '@hugeicons/core-free-icons';
import { getTrialState, getTrialNudgeCopy, getTrialHoursLeft, getTrialDaysLeft } from '@overlayqa/shared';
import { useBillingInfo } from '../hooks/useBillingInfo';
import { capture } from '../lib/analytics';
import { log } from '@/lib/logger';

const SNOOZE_STORAGE_KEY = 'oq_trial_nudge_snooze_until';
const SNOOZE_DURATION_MS = 60 * 60 * 1000; // 1 hour

function readSnoozeUntil(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = window.sessionStorage.getItem(SNOOZE_STORAGE_KEY);
    if (!raw) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeSnoozeUntil(value: number) {
  try {
    window.sessionStorage.setItem(SNOOZE_STORAGE_KEY, String(value));
  } catch {
    // sessionStorage unavailable (incognito/quota) — silently skip
  }
}

/**
 * Persistent top-of-app banner that nudges trial users to pick a plan.
 *
 * States (computed via shared getTrialState):
 *   - paid      → renders nothing (includes >4 days left: suppressed)
 *   - safe      → neutral grey, dismissible (3-4 days left)
 *   - warning   → amber, dismissible (1h snooze via sessionStorage)
 *   - ending-today → red, dismissible
 *   - expired   → red, NOT dismissible
 *
 * Suppression rules:
 *   - Hidden on /billing (don't shout at someone already looking)
 *   - Hidden for non-owners (computed in getTrialState)
 *   - Hidden while billing info loads (no flash on first paint)
 */
export function TrialNudgeBanner() {
  const { data: info } = useBillingInfo();
  const location = useLocation();
  const [snoozeUntil, setSnoozeUntil] = useState<number>(() => readSnoozeUntil());
  const [hasFiredView, setHasFiredView] = useState(false);

  // Compute state from billing info. If info isn't loaded yet, render nothing.
  const trialState = info
    ? getTrialState({
        plan: info.plan,
        betaExpiresAt: info.betaExpiresAt,
        isOwner: info.isOwner,
        trialTimeZone: info.trialTimeZone,
      })
    : 'paid';

  const hoursLeft = getTrialHoursLeft(info?.betaExpiresAt ?? null);
  // issuesCaptured decorates the calm state only ("15 days left · 24 issues
  // captured"). It is a report of work done, NOT a quota — trials are
  // maxIssuesPerProject: -1. getTrialNudgeCopy drops the clause at 0 and for
  // every escalated state, so an older API response without the field, or a
  // failed count, degrades to the plain runway sentence.
  const copy = getTrialNudgeCopy(trialState, hoursLeft, info?.issuesCaptured,
    getTrialDaysLeft(info?.betaExpiresAt ?? null, info?.trialTimeZone));

  // Don't show on /billing — they're already on the right page.
  const isOnBillingPage = location.pathname.startsWith('/billing');
  const isSnoozed = trialState !== 'expired' && Date.now() < snoozeUntil;
  // Every ACTIVE-trial state now belongs to TrialStatusChip in the header row
  // (Emily, 2026-08-31). What is left here is the pair of states that are not
  // nudges at all and must not shrink into a corner badge: an expired
  // workspace, which is read-only because every write 403s with TRIAL_EXPIRED,
  // and the non-owner read-only note below. Neither was ever dismissible, so
  // keeping them full-width is consistent with "not a full dismissable banner".
  const shouldRender =
    trialState === 'expired' && copy !== null && !isOnBillingPage && !isSnoozed;

  // Non-owners get no upgrade nudge (getTrialState suppresses them by design:
  // they can't act on it). But an expired workspace is read-only for them too,
  // and silence reads as breakage — so they get a quiet status line naming the
  // cause and who can act. No CTA, dismissible like the other states.
  const isNonOwnerLocked = !!info && !info.isOwner && info.plan === null;
  const showNonOwnerNote =
    isNonOwnerLocked && !isOnBillingPage && Date.now() >= snoozeUntil;

  useEffect(() => {
    if (!showNonOwnerNote || hasFiredView) return;
    setHasFiredView(true);
    capture('trial_nudge_shown', { state: 'expired-non-owner', hours_left: 0 });
    log.info('[TrialNudgeBanner] non-owner read-only note shown');
  }, [showNonOwnerNote, hasFiredView]);

  // Fire one analytics event per session per state transition so we can
  // measure how often the banner gets in front of users (and how often
  // they click). Don't fire when suppressed — that's noise.
  useEffect(() => {
    if (!shouldRender || hasFiredView) return;
    setHasFiredView(true);
    capture('trial_nudge_shown', {
      state: trialState,
      hours_left: Math.round(hoursLeft),
    });
    log.info('[TrialNudgeBanner] shown', { state: trialState });
  }, [shouldRender, hasFiredView, trialState, hoursLeft]);

  const handleDismiss = useCallback(() => {
    const until = Date.now() + SNOOZE_DURATION_MS;
    writeSnoozeUntil(until);
    setSnoozeUntil(until);
    capture('trial_nudge_dismissed', { state: trialState });
  }, [trialState]);

  const handleCTA = useCallback(() => {
    capture('trial_nudge_cta_clicked', { state: trialState });
  }, [trialState]);

  if (showNonOwnerNote) {
    return (
      <div
        role="status"
        className="flex items-center justify-center gap-3 px-6 py-3 text-sm bg-gray-100 text-gray-700"
      >
        <span className="inline-flex items-center gap-2">
          <HugeiconsIcon
            icon={LockedIcon}
            size={16}
            strokeWidth={2}
            color="currentColor"
            className="shrink-0 text-gray-500"
          />
          <span>
            This workspace is read-only until a team owner picks a plan.
          </span>
        </span>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss for 1 hour"
          className="ml-1 p-1 rounded hover:bg-gray-200 transition-colors"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={14} strokeWidth={2} color="currentColor" />
        </button>
      </div>
    );
  }

  if (!shouldRender || !copy) return null;

  const isExpired = copy.variant === 'expired';
  const isDismissible = !isExpired;

  // Values live in styles/index.css as --color-trial-gradient-*, mirroring the
  // extension's --oq-trial-gradient-*. The extension renders the same four
  // variants and the two banners must not drift; see the token comment.
  const GRADIENT_BG: Record<string, string> = {
    info: 'var(--color-trial-gradient-info)',
    warning: 'var(--color-trial-gradient-warning)',
    urgent: 'var(--color-trial-gradient-urgent)',
    expired: 'var(--color-trial-gradient-expired)',
  };
  const bannerIcon = isExpired ? LockedIcon : copy.variant === 'info' ? Clock01Icon : Alert02Icon;

  return (
    <div
      role={isExpired ? 'alert' : 'status'}
      className="flex items-center justify-center gap-3 px-6 py-3 text-sm text-white"
      style={{ background: GRADIENT_BG[copy.variant] }}
    >
      <span className="inline-flex items-center gap-2">
        <HugeiconsIcon
          icon={bannerIcon}
          size={16}
          strokeWidth={2}
          color={PALETTE.surface.card}
          className="shrink-0"
        />
        <span className="font-medium">{copy.title}</span>
      </span>
      <Link
        to="/billing"
        onClick={handleCTA}
        className="inline-flex items-center gap-1 font-semibold text-white underline-offset-2 hover:underline"
      >
        <span className="text-cap-trim">{copy.cta}</span>
        <HugeiconsIcon icon={ArrowRight01Icon} size={14} strokeWidth={2} color={PALETTE.surface.card} />
      </Link>
      {isDismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss for 1 hour"
          className="ml-1 p-1 rounded hover:bg-white/15 transition-colors"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={14} strokeWidth={2} color={PALETTE.surface.card} />
        </button>
      )}
    </div>
  );
}
